import React, { useEffect, useCallback } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';

import {
    locale,
    hasGapAnalysisData,
    getGapDateRangesFromPeriodSelection,
    setInLocalStorage,
    getGapAnalysisLocalStorageKey,
} from '@trqk/utils';
import { queryStrings } from '@trqk/resolvers';
import { LabeledValue, ValidatedInput } from '@trqk/components';
import { useInputValidation } from '@trqk/hooks';
import LIST_GAP_ANALYSIS_FILTERS from '@trqk/api/src/graphql/operations/listGapAnalysisFilters.gql';

import { FULL_PATHS, HASHES } from '../../../../../../constants';
import { DataStatus } from './DataStatus';
import { dataSourceLocaleStringOptions } from '../../../constants';
import { getGapDataSourceLabels } from '../../../utils';

import { StepWrapper } from '../..';
import {
    useEntities,
    usePaymentPlanStatus,
    useToast,
    useGapAnalysisTools,
} from '../../../../../../hooks';
import { inputIds } from './constants';

const { GET_SESSION_CURRENT_USER } = queryStrings;

/**
 * Displays details for the performance and detection data that will be used to create a Gap
 * Analysis report. Allows the user to upload a statement and/or detection data if necessary.
 * @param props - {@link GapAnalysisStepProps}
 */
export const SetUp: GapAnalysisStepComponent = props => {
    const { context, remove, send, stepId } = props;
    const {
        reportDescription,
        selectedPeriod,
        trqkGap,
        uploadedDetectionFile,
    } = context;
    const gapAnalysisId = trqkGap?.gap_analysis_id ?? null;
    const client = useApolloClient();
    const history = useHistory();
    const { add: addToast } = useToast();
    const { selectedEntityId } = useEntities();
    const { canUploadDetections } = usePaymentPlanStatus();
    const { analyzeTrqkGap } = useGapAnalysisTools();
    const dateRanges = getGapDateRangesFromPeriodSelection(selectedPeriod);
    const { hasDetectionData, hasPerformanceData } = hasGapAnalysisData(
        selectedPeriod,
        trqkGap,
    );

    const hasPerformancesAndDetections = hasDetectionData && hasPerformanceData;

    useEffect(() => {
        send({
            type: 'UPDATE_STEP',
            value: {
                stepId,
                update: {
                    disableNextStep: !hasPerformancesAndDetections,
                },
            },
        });
    }, [hasPerformancesAndDetections]);

    const sourceLabels = getGapDataSourceLabels({
        selectedPeriod,
        localeStringOptions: dataSourceLocaleStringOptions,
        trqkGap,
    });

    const currentUserQuery = useQuery<{ session: { currentUser: User } }>(
        GET_SESSION_CURRENT_USER,
    );
    const userId = currentUserQuery?.data?.session?.currentUser?.id ?? null;
    const createGapAnalysisLocalStorageKey = getGapAnalysisLocalStorageKey(
        userId,
        selectedEntityId,
    );

    let description: JSX.Element | null = null;

    if (
        dateRanges.performancePeriod.end !== null &&
        dateRanges.performancePeriod.start !== null
    ) {
        description = (
            <p>
                {
                    locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.SET_UP.DESCRIPTION
                        .DATA_FROM
                }{' '}
                <span className="font-bold">
                    {dateRanges.performancePeriod.start.toLocaleString(
                        'en',
                        dataSourceLocaleStringOptions,
                    )}
                </span>{' '}
                {locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.SET_UP.DESCRIPTION.TO}{' '}
                <span className="font-bold">
                    {dateRanges.performancePeriod.end.toLocaleString(
                        'en',
                        dataSourceLocaleStringOptions,
                    )}
                </span>
                .
            </p>
        );
    }

    const detectionsCTAButtonLabel = hasDetectionData
        ? locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.SET_UP.CTA.REPLACE_DETECTIONS
        : locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.SET_UP.CTA.UPLOAD_DETECTIONS;

    const detectionsCTADescription = hasDetectionData
        ? locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.SET_UP.CTA
              .CONFIRM_OR_REPLACE_DETECTIONS
        : locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.SET_UP.CTA.NO_DETECTIONS;

    const { handleSubmit, validationOutputs } = useInputValidation({
        inputs: [
            {
                inputId: inputIds.reportName,
                label: 'Report Name',
                required: true,
                type: 'name',
                value: reportDescription,
            },
        ],
    });

    const listGapAnalysisFilters = useCallback(
        (): Promise<Maybe<GapAnalysisFilter[]>> =>
            new Promise(resolve => {
                if (gapAnalysisId) {
                    client
                        .query<{ listGapAnalysisFilters: GapAnalysisFilter[] }>(
                            {
                                query: LIST_GAP_ANALYSIS_FILTERS,
                                variables: {
                                    gap_analysis_id: gapAnalysisId,
                                    asset_url: uploadedDetectionFile,
                                },
                                fetchPolicy: 'network-only',
                            },
                        )
                        .then(results => {
                            const filters =
                                results?.data?.listGapAnalysisFilters ?? null;

                            if (filters === null) {
                                addToast({
                                    kind: 'warning-banner',
                                    content:
                                        locale.enUS.API_ERRORS
                                            .listGapAnalysisFilters,
                                    logMessage: 'listGapAnalysisFilters',
                                });
                            }

                            resolve(filters);
                        })
                        .catch(() => {
                            addToast({
                                kind: 'warning-banner',
                                content:
                                    locale.enUS.API_ERRORS
                                        .listGapAnalysisFilters,
                                logMessage: 'listGapAnalysisFilters',
                            });
                            resolve(null);
                        });
                } else {
                    resolve(null);
                }
            }),
        [gapAnalysisId, uploadedDetectionFile],
    );

    const beforeNextStep = useCallback(
        (): Promise<boolean> =>
            new Promise(resolve => {
                handleSubmit({
                    onError: () => {
                        resolve(false);
                    },
                    onSuccess: () => {
                        listGapAnalysisFilters().then(result => {
                            send({
                                type: 'SET_FILTER_OPTIONS',
                                value: {
                                    filterOptions: result ?? [],
                                },
                            });

                            if (result === null) {
                                resolve(false);
                            } else if (result.length > 0) {
                                send({
                                    type: 'GO_TO_STEP',
                                    value: {
                                        stepId: 'selectOptions',
                                    },
                                });

                                resolve(false);
                            } else {
                                analyzeTrqkGap(gapAnalysisId, null)
                                    .then(success => {
                                        resolve(success);
                                    })
                                    .catch(() => {
                                        resolve(false);
                                    });
                            }
                        });
                    },
                });
            }),
        [analyzeTrqkGap, gapAnalysisId, listGapAnalysisFilters],
    );

    const filename = trqkGap?.detection_batch?.filename ?? null;
    const performancesDateRange = sourceLabels.performances?.dateRange ?? null;
    const statementPeriodLabel =
        sourceLabels.performances?.statementPeriodLabel ?? null;
    const pro = sourceLabels.performances?.pro ?? null;
    const detectionsDateRange = sourceLabels.detections?.dateRange ?? null;

    return (
        <StepWrapper
            beforeNextStep={beforeNextStep}
            context={context}
            remove={remove}
            send={send}
        >
            <div className="flex-grow flex flex-col">
                <div className="text-sm mb-6">{description}</div>
                <div className="space-y-6">
                    <div className="flex-none">
                        <ValidatedInput
                            addClasses="border border-tertiary-200 rounded-sm"
                            expandLabel={false}
                            handleChange={(value: string) => {
                                send({
                                    type: 'SET_REPORT_DESCRIPTION',
                                    value: {
                                        reportDescription: value,
                                    },
                                });
                            }}
                            inputId={inputIds.reportName}
                            label={
                                locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.SET_UP
                                    .REPORT_NAME_INPUT.LABEL
                            }
                            labelClasses="font-bold text-lg text-tertiary-500 tracking-wider"
                            placeholder={
                                locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.SET_UP
                                    .REPORT_NAME_INPUT.PLACEHOLDER
                            }
                            showErrorsAs="tooltip"
                            value={reportDescription}
                            validationOutputs={validationOutputs}
                            width={400}
                        />
                    </div>
                    <DataStatus
                        dataTitle={
                            locale.enUS.MANAGE_GAP_ANALYSIS.DATA_LABELS
                                .PERFORMANCES
                        }
                        missingLabel={sourceLabels.performances.missingLabel}
                        hasData={hasPerformanceData}
                        callToActionConfig={{
                            buttonLabel:
                                locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.SET_UP.CTA
                                    .UPLOAD_STATEMENT,
                            description:
                                locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.SET_UP.CTA
                                    .NO_PERFORMANCES,
                            handleClick: () => {
                                // Persist the current context in localStorage so it can be restored
                                // when the user returns after uploading a statement
                                setInLocalStorage(
                                    createGapAnalysisLocalStorageKey,
                                    (): string => JSON.stringify(context),
                                );

                                remove({
                                    canceled: false,
                                    success: true,
                                });

                                history.push({
                                    pathname:
                                        FULL_PATHS.MANAGE_STATEMENTS_CHARTS,
                                    hash: HASHES.MANAGE_STATEMENTS.UPLOADS,
                                    search: history.location.search,
                                    state: {
                                        createGapAnalysis: {
                                            statementPeriodLabel,
                                            performancePeriod:
                                                dateRanges.performancePeriod,
                                            pro,
                                        },
                                    },
                                });
                            },
                        }}
                        showCallToAction={!hasPerformanceData}
                    >
                        <div className="flex space-x-4">
                            {performancesDateRange && (
                                <LabeledValue
                                    label={
                                        locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                            .SET_UP.DATA_LABELS.DATE_RANGE
                                    }
                                    value={performancesDateRange}
                                    maxWidth={300}
                                />
                            )}
                            {pro && statementPeriodLabel && (
                                <LabeledValue
                                    label={
                                        locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                            .SET_UP.DATA_LABELS.STATEMENT
                                    }
                                    value={`${pro} ${statementPeriodLabel}`}
                                    maxWidth={300}
                                />
                            )}
                        </div>
                    </DataStatus>
                    <DataStatus
                        dataTitle={
                            locale.enUS.MANAGE_GAP_ANALYSIS.DATA_LABELS
                                .DETECTIONS
                        }
                        missingLabel={sourceLabels.detections.missingLabel}
                        hasData={hasDetectionData}
                        callToActionConfig={{
                            buttonLabel: detectionsCTAButtonLabel,
                            description: detectionsCTADescription,
                            handleClick: () => {
                                send({
                                    type: 'GO_TO_NEXT_STEP',
                                    value: {
                                        nextStepId: 'uploadDetections',
                                    },
                                });
                            },
                        }}
                        showCallToAction={canUploadDetections}
                    >
                        <div className="flex space-x-4">
                            {detectionsDateRange && (
                                <LabeledValue
                                    label={
                                        locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                            .SET_UP.DATA_LABELS.DATE_RANGE
                                    }
                                    value={detectionsDateRange}
                                    maxWidth={220}
                                />
                            )}
                            {filename && (
                                <LabeledValue
                                    label={
                                        locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                            .SET_UP.DATA_LABELS.FILE_NAME
                                    }
                                    value={filename}
                                    maxWidth={210}
                                />
                            )}
                        </div>
                    </DataStatus>
                </div>
            </div>
        </StepWrapper>
    );
};
